import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, Clock4, Ban, LayoutDashboard, BookOpen, CalendarDays, Armchair, Hourglass, ShoppingBag, ChefHat, Users, Megaphone, Boxes, Truck, UtensilsCrossed, CalendarClock, BarChart3, UserCog, Settings as SettingsIcon, CreditCard, Ticket } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Overview from "./dashboard/Overview";
import BookingsTab from "./dashboard/BookingsTab";
import CalendarTab from "./dashboard/CalendarTab";
import FloorPlan from "./dashboard/FloorPlan";
import GuestsTab from "./dashboard/GuestsTab";
import QueueTab from "./dashboard/QueueTab";
import OrdersTab from "./dashboard/OrdersTab";
import KitchenTab from "./dashboard/KitchenTab";
import InventoryTab from "./dashboard/InventoryTab";
import RotaTab from "./dashboard/RotaTab";
import MenusTab from "./dashboard/MenusTab";
import EventsTab from "./dashboard/EventsTab";
import SuppliersTab from "./dashboard/SuppliersTab";
import ReportsTab from "./dashboard/ReportsTab";
import StaffTab from "./dashboard/StaffTab";
import SettingsTab from "./dashboard/SettingsTab";
import BillingTab from "./dashboard/BillingTab";
import MarketingTab from "./dashboard/MarketingTab";

function CreateRestaurant({ onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", cuisine: "", address: "", city: "", phone: "", image_url: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/restaurants", form);
      toast.success("Restaurant submitted for approval");
      onCreated();
    } catch (err) {
      toast.error(fmtErr(err));
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-restaurant-form">
      <Store className="w-10 h-10 text-primary" />
      <h2 className="font-display text-3xl tracking-tight mt-4">List your restaurant</h2>
      <p className="text-muted-foreground mt-2 text-sm">Our team reviews every venue before it goes live — usually within a day.</p>
      <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Restaurant name</Label>
          <Input data-testid="rest-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1.5 bg-card border-border/60" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Cuisine</Label>
          <Input data-testid="rest-cuisine-input" value={form.cuisine} onChange={(e) => setForm({ ...form, cuisine: e.target.value })}
            className="mt-1.5 bg-card border-border/60" placeholder="e.g. Italian" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Phone</Label>
          <Input data-testid="rest-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="mt-1.5 bg-card border-border/60" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Address</Label>
          <Input data-testid="rest-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="mt-1.5 bg-card border-border/60" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">City</Label>
          <Input data-testid="rest-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="mt-1.5 bg-card border-border/60" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Image URL</Label>
          <Input data-testid="rest-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            className="mt-1.5 bg-card border-border/60" placeholder="https://..." />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Description</Label>
          <Textarea data-testid="rest-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 bg-card border-border/60" />
        </div>
        <Button data-testid="rest-submit-btn" type="submit" disabled={loading} className="sm:col-span-2 rounded-full">
          {loading ? "Submitting..." : "Submit for approval"}
        </Button>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("calendar");

  const load = () => {
    api.get("/restaurants/mine")
      .then((r) => setRestaurant(r.data))
      .catch(() => setRestaurant(null))
      .finally(() => setLoading(false));
    api.get("/restaurants/mine/all").then((r) => setVenues(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="min-h-screen bg-background" />;

  if (!restaurant && user.role === "owner") {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 min-h-screen">
        <CreateRestaurant onCreated={load} />
      </div>
    );
  }

  if (!restaurant) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-muted-foreground">No restaurant assigned to your account.</div>;
  }

  if (restaurant.status === "pending") {
    return (
      <div data-testid="pending-notice" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Clock4 className="w-12 h-12 text-amber-300 mx-auto" />
        <h2 className="font-display text-3xl mt-6">{restaurant.name} is under review</h2>
        <p className="text-muted-foreground mt-3">We'll email you as soon as your venue is approved and bookable.</p>
      </div>
    );
  }

  if (restaurant.status === "suspended") {
    return (
      <div data-testid="suspended-notice" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Ban className="w-12 h-12 text-destructive mx-auto" />
        <h2 className="font-display text-3xl mt-6">{restaurant.name} is suspended</h2>
        <p className="text-muted-foreground mt-3">Contact platform support to reinstate your listing.</p>
      </div>
    );
  }

  const isHost = user.role === "host";
  const isOwner = user.role === "owner";

  const NAV_GROUPS = [
    { label: "Service", items: [["overview", "Business hub", LayoutDashboard], ["bookings", "Bookings", BookOpen], ["calendar", "Calendar", CalendarDays], ["floorplan", "Floor plan", Armchair], ["queue", "Queue", Hourglass], ["orders", "Orders", ShoppingBag], ["kitchen", "Kitchen", ChefHat]] },
    ...(!isHost ? [
      { label: "Guests", items: [["guests", "Guests", Users], ["events", "Events", Ticket], ["marketing", "Marketing", Megaphone]] },
      { label: "Stock", items: [["inventory", "Inventory", Boxes], ["suppliers", "Suppliers", Truck], ["menus", "Menus", UtensilsCrossed]] },
      { label: "Manage", items: [["rota", "Rota", CalendarClock], ["reports", "Reports", BarChart3], ...(isOwner ? [["staff", "Staff", UserCog]] : []), ["settings", "Settings", SettingsIcon], ...(isOwner ? [["billing", "Billing", CreditCard]] : [])] },
    ] : []),
  ];

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 min-h-screen">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-primary">{restaurant.subscription_tier} plan</p>
          <h1 className="font-outfit font-semibold text-3xl tracking-tight mt-1">{restaurant.name}</h1>
        </div>
        {isOwner && venues.length > 0 && (
          <div className="flex items-center gap-2" data-testid="venue-switcher">
            {venues.length > 1 && (
              <Select value={restaurant.id}
                onValueChange={(v) => {
                  const venue = venues.find((x) => x.id === v);
                  if (venue) {
                    setRestaurant(venue);
                    setTab("overview");
                  }
                }}>
                <SelectTrigger data-testid="venue-switcher-select" className="w-[200px] bg-card border-border/60 rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={v.id} data-testid={`venue-option-${v.id}`}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="add-venue-btn" size="sm" variant="outline" className="rounded-full"
              onClick={() => setRestaurant(null)}>
              + Add venue
            </Button>
          </div>
        )}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <div className="lg:flex lg:gap-8 lg:items-start">
        <nav data-testid="dashboard-nav" className="hidden lg:flex flex-col gap-5 w-48 shrink-0 sticky top-24">
          {NAV_GROUPS.map((g) => (
            <div key={g.label}>
              <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-3 mb-1.5">{g.label}</p>
              <div className="flex flex-col gap-0.5">
                {g.items.map(([v, label, Icon]) => (
                  <button key={v} data-testid={`tab-${v}`} onClick={() => setTab(v)}
                    className={`flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-sm transition-colors ${tab === v ? "bg-primary text-primary-foreground font-medium shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-card"}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <TabsList data-testid="dashboard-tabs" className="lg:hidden bg-card border border-border/60 w-full max-w-full overflow-x-auto flex-nowrap justify-start pr-10 dash-scroll"
          style={{ maskImage: "linear-gradient(to right, black calc(100% - 56px), rgba(0,0,0,0.15))" }}>
          {NAV_GROUPS.flatMap((g) => g.items).map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`mtab-${v}`} className="gap-1.5">
              <Icon className="w-3.5 h-3.5" />{label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex-1 min-w-0">
        <TabsContent value="overview" className="mt-8"><Overview restaurant={restaurant} onSaved={load} /></TabsContent>
        <TabsContent value="bookings" className="mt-8"><BookingsTab restaurant={restaurant} /></TabsContent>
        <TabsContent value="calendar" className="mt-8"><CalendarTab restaurant={restaurant} canEdit={!isHost} onSaved={load} /></TabsContent>
        <TabsContent value="floorplan" className="mt-8"><FloorPlan restaurant={restaurant} canEdit={!isHost} /></TabsContent>
        <TabsContent value="queue" className="mt-8"><QueueTab restaurant={restaurant} /></TabsContent>
        <TabsContent value="orders" className="mt-8"><OrdersTab restaurant={restaurant} /></TabsContent>
        <TabsContent value="kitchen" className="mt-8"><KitchenTab restaurant={restaurant} /></TabsContent>
        {!isHost && <TabsContent value="guests" className="mt-8"><GuestsTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="marketing" className="mt-8"><MarketingTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="inventory" className="mt-8"><InventoryTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="rota" className="mt-8"><RotaTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="menus" className="mt-8"><MenusTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="events" className="mt-8"><EventsTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="suppliers" className="mt-8"><SuppliersTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="reports" className="mt-8"><ReportsTab restaurant={restaurant} /></TabsContent>}
        {isOwner && <TabsContent value="staff" className="mt-8"><StaffTab restaurant={restaurant} /></TabsContent>}
        {!isHost && <TabsContent value="settings" className="mt-8"><SettingsTab restaurant={restaurant} onSaved={load} /></TabsContent>}
        {isOwner && <TabsContent value="billing" className="mt-8"><BillingTab restaurant={restaurant} onChanged={load} /></TabsContent>}
        </div>
        </div>
      </Tabs>
    </div>
  );
}
